import { addProtocol, Map as MlMap } from "maplibre-gl";
import { PMTiles } from "pmtiles";
import { DemPack, getArchive, getDemPacks } from "../terrain/dem-packs";

export const DEM_PROTOCOL = "pmtiles-dem";

// rio-rgbify's default is 512×512 — the producer passes no `--tile-size`.
// Every PNG returned from the protocol handler (native AND synthesised)
// MUST be exactly this size or MapLibre's `backfillBorder` rejects the
// border-stitch step with `Error: dem dimension mismatch`.
const TILE_PX = 512;

// Cap on how many zoom levels below the archive's `min_zoom` we'll synthesize
// tiles for. delta=2 → 4×4 = 16 child tiles fetched + composited onto a
// 2048×2048 canvas (~16 MB RGBA). For an AT pack with archive min_zoom=11,
// this exposes terrain from z=9 upward. Below that the source declares no
// coverage and MapLibre leaves terrain flat. Raising delta to 3 puts the
// composite canvas at 4096² (~64 MB) — out of budget on mobile.
const DOWNSAMPLE_MAX_DELTA = 2;

// Bounded cache for synthesised tiles. Each entry is a PNG (~50–150 KB),
// 512 entries ≈ 30–75 MB worst case.
const SYNTH_CACHE_MAX = 512;

let protocolRegistered = false;
const installedSourceIds = new Set<string>();
const archiveMinZoom = new Map<string, number>();
const synthCache = new Map<string, Uint8Array>();

export function resetDemSourceState(): void {
  installedSourceIds.clear();
  archiveMinZoom.clear();
  synthCache.clear();
}

export function sourceIdFor(pack: DemPack): string {
  return `dem-pmtiles:${pack.manifest.id}`;
}

// Terrain-specific source variant. MapLibre warns when the same source
// backs both a hillshade layer and `setTerrain` — its worker pipeline
// runs different post-decode steps in the two cases, and aliasing one
// source for both hurts caching. Both ids point at the same archive,
// so the underlying fetch + getZxy are shared via the archive cache.
export function terrainSourceIdFor(pack: DemPack): string {
  return `dem-pmtiles-terrain:${pack.manifest.id}`;
}

// Whether this engine's `createImageBitmap` can decode lossless (VP8L) WebP.
// Historically Android System WebView could NOT (it threw / yielded a 0-width
// bitmap), so this protocol always transcoded the packs' VP8L tiles to PNG
// before handing them to MapLibre — whose worker decodes via createImageBitmap.
// That bug is fixed on modern Chromium WebView (verified 2026-06-08, Chrome 148
// WebView: real terrain-RGB VP8L tile decodes correctly), and iOS WebKit never
// had it. When the engine can decode VP8L we serve native tiles RAW — MapLibre's
// own decoder handles them — skipping a full decode + PNG re-encode per tile.
// The HTMLImageElement transcode stays as the fallback for any old engine that
// still trips the bug. Detection is a one-shot memoised probe of a 16×16 VP8L.
let vp8lDecodeProbe: Promise<boolean> | null = null;

const VP8L_PROBE_DATA_URL = "data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvD8ADAAdQiirUo/+BiOh/AAA=";

function canDecodeVp8lViaImageBitmap(): Promise<boolean> {
  if (vp8lDecodeProbe) return vp8lDecodeProbe;
  vp8lDecodeProbe = (async () => {
    if (typeof createImageBitmap !== "function") return false;
    try {
      const blob = await fetch(VP8L_PROBE_DATA_URL).then((r) => r.blob());
      const bm = await createImageBitmap(blob);
      // The bug surfaced as a throw OR a 0×0 bitmap; the probe is 16×16.
      const ok = bm.width === 16 && bm.height === 16;
      bm.close?.();
      return ok;
    } catch {
      return false;
    }
  })();
  return vp8lDecodeProbe;
}

/**
 * Decode the pack's lossless (VP8L) WebP into an HTMLImageElement. Used as the
 * fallback transcode path on engines whose `createImageBitmap` can't decode
 * VP8L (legacy Android System WebView). Same constraint as terrain-pmtiles.ts.
 */
export async function webpToImage(buffer: ArrayBuffer): Promise<HTMLImageElement | null> {
  const blob = new Blob([buffer], { type: "image/webp" });
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("webp decode failed"));
      image.src = url;
    });
    if (image.naturalWidth === 0) return null;
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function canvasToPng(canvas: HTMLCanvasElement): Promise<Uint8Array | null> {
  const pngBlob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!pngBlob) return null;
  return new Uint8Array(await pngBlob.arrayBuffer());
}

async function transcodeNativeTile(buffer: ArrayBuffer): Promise<Uint8Array | null> {
  const image = await webpToImage(buffer);
  if (!image) return null;
  // Force TILE_PX even when the archive's native tile size differs (current
  // packs are 512 from rio-rgbify defaults; this is defence-in-depth). Both
  // paths through this protocol handler must agree on dim or MapLibre's
  // backfillBorder rejects the source with `dem dimension mismatch`.
  const canvas = document.createElement("canvas");
  canvas.width = TILE_PX;
  canvas.height = TILE_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  if (image.naturalWidth !== TILE_PX || image.naturalHeight !== TILE_PX) {
    // Bilinear of color channels is approximately linear in elevation
    // (R*65536+G*256+B is monotonic-linear), with one byte of LSB
    // quantisation in B — acceptable for terrain shading.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, TILE_PX, TILE_PX);
  } else {
    ctx.drawImage(image, 0, 0);
  }
  return canvasToPng(canvas);
}

/**
 * Synthesise a tile at (z, x, y) by fetching the `factor × factor` z=archiveMinZoom
 * children that cover the same geographic area, compositing them onto a
 * (TILE_PX·factor)² canvas, and downsampling to TILE_PX² via browser bilinear.
 *
 * Terrain-RGB encodes elevation linearly across the 24-bit RGB integer; a
 * per-channel bilinear average is therefore a linear average of the decoded
 * elevation (within ~128 m of byte-level quantisation in G, ~0.5 m in B).
 */
async function synthesizeLowerZoomTile(
  archive: PMTiles,
  archiveZ: number,
  z: number,
  x: number,
  y: number
): Promise<Uint8Array | null> {
  const delta = archiveZ - z;
  if (delta <= 0 || delta > DOWNSAMPLE_MAX_DELTA) return null;
  const factor = 1 << delta;
  const bigPx = TILE_PX * factor;

  const big = document.createElement("canvas");
  big.width = bigPx;
  big.height = bigPx;
  const bigCtx = big.getContext("2d");
  if (!bigCtx) return null;
  // Sea level (stored 10000 → R=0, G=39, B=16) so missing children don't decode as -10000 m.
  bigCtx.fillStyle = "rgb(0,39,16)";
  bigCtx.fillRect(0, 0, bigPx, bigPx);

  const startX = x * factor;
  const startY = y * factor;
  let anyLoaded = false;

  // Each child fetch+decode is isolated. A single transient PMTiles read
  // error or a corrupt WebP must not collapse the whole parent tile —
  // leave the failed child at the sea-level fill and emit the rest.
  const fetches: Promise<void>[] = [];
  for (let dy = 0; dy < factor; dy++) {
    for (let dx = 0; dx < factor; dx++) {
      fetches.push(
        (async () => {
          try {
            const resp = await archive.getZxy(archiveZ, startX + dx, startY + dy);
            if (!resp) return;
            const img = await webpToImage(resp.data);
            if (!img) return;
            bigCtx.drawImage(img, dx * TILE_PX, dy * TILE_PX);
            anyLoaded = true;
          } catch (err) {
            console.warn(
              `[dem-source] child ${archiveZ}/${startX + dx}/${startY + dy} decode failed; leaving sea-level`,
              err
            );
          }
        })()
      );
    }
  }
  await Promise.all(fetches);
  if (!anyLoaded) return null;

  const out = document.createElement("canvas");
  out.width = TILE_PX;
  out.height = TILE_PX;
  const outCtx = out.getContext("2d");
  if (!outCtx) return null;
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = "high";
  outCtx.drawImage(big, 0, 0, bigPx, bigPx, 0, 0, TILE_PX, TILE_PX);
  return canvasToPng(out);
}

function cachePut(key: string, png: Uint8Array): void {
  if (synthCache.size >= SYNTH_CACHE_MAX) {
    const firstKey = synthCache.keys().next().value;
    if (firstKey !== undefined) synthCache.delete(firstKey);
  }
  synthCache.set(key, png);
}

function registerProtocol(): void {
  if (protocolRegistered) return;
  protocolRegistered = true;

  addProtocol(DEM_PROTOCOL, async (params) => {
    // Outer try/catch so a rejection here (malformed URL, archive header
    // fetch failure, decode crash) becomes an empty tile instead of
    // propagating into MapLibre and flooding the console with tile errors
    // for the whole viewport.
    try {
      const raw = params.url.slice(DEM_PROTOCOL.length + 3);
      const hash = raw.lastIndexOf("#");
      if (hash < 0) throw new Error(`dem-source: malformed url ${params.url}`);
      const archiveUrl = raw.slice(0, hash);
      const zxy = raw.slice(hash + 1).split("/");
      if (zxy.length !== 3) throw new Error(`dem-source: malformed zxy in ${params.url}`);

      const z = +zxy[0];
      const x = +zxy[1];
      const y = +zxy[2];
      const archive = getArchive(archiveUrl);
      const archiveZ = archiveMinZoom.get(archiveUrl) ?? z;

      if (z >= archiveZ) {
        const resp = await archive.getZxy(z, x, y);
        if (!resp) return { data: new Uint8Array() };
        // Fast path: when the engine can decode VP8L, hand MapLibre the native
        // WebP untouched (it decodes via createImageBitmap, which sniffs the
        // bytes regardless of the blob MIME). Skips a decode + PNG re-encode.
        // Relies on the pack's native tile being TILE_PX² (rio-rgbify default);
        // the same assumption the `tileSize: TILE_PX` source declaration makes.
        if (await canDecodeVp8lViaImageBitmap()) {
          return { data: new Uint8Array(resp.data) };
        }
        const png = await transcodeNativeTile(resp.data);
        return { data: png ?? new Uint8Array() };
      }

      const cacheKey = `${archiveUrl}|${z}/${x}/${y}`;
      const cached = synthCache.get(cacheKey);
      if (cached) return { data: cached };

      const png = await synthesizeLowerZoomTile(archive, archiveZ, z, x, y);
      if (!png) return { data: new Uint8Array() };
      cachePut(cacheKey, png);
      return { data: png };
    } catch (err) {
      console.warn(`[dem-source] ${params.url} failed; serving empty tile`, err);
      return { data: new Uint8Array() };
    }
  });
}

/**
 * Idempotent: registers the pmtiles-dem protocol on first call and adds one
 * `raster-dem` source per installed pack. Both hillshade and 3D-terrain
 * features consume these shared sources by id (`sourceIdFor(pack)`).
 *
 * The source is declared with a lowered `minzoom` (down to `archiveMinZoom -
 * DOWNSAMPLE_MAX_DELTA`) — the protocol handler synthesises those lower-zoom
 * tiles from the archive's actual data on demand.
 */
export async function ensureDemSources(map: MlMap): Promise<DemPack[]> {
  registerProtocol();
  const packs = await getDemPacks();
  for (const pack of packs) {
    const sourceId = sourceIdFor(pack);
    if (map.getSource(sourceId)) continue;

    const archiveUrl = pack.archiveUrl;
    const archiveZ = pack.manifest.min_zoom;
    const minzoom = Math.max(0, archiveZ - DOWNSAMPLE_MAX_DELTA);
    const maxzoom = pack.manifest.max_zoom;
    archiveMinZoom.set(archiveUrl, archiveZ);

    // Our packs encode terrain-RGB as `elev = R*65536 + G*256 + B - 10000`.
    // MapLibre's formula is `elev = R*redFactor + G*greenFactor + B*blueFactor
    // − baseShift` — sign on the shift is opposite the pack manifest's "offset"
    // convention, so the manifest's `offset: -10000` corresponds to
    // `baseShift: 10000` here. Without `encoding: "custom"` MapLibre would use
    // the Mapbox defaults (scale 0.1) and decode mountains at 1/10 height.
    map.addSource(sourceId, {
      type: "raster-dem",
      // rio-rgbify default tile size is 512×512 (producer passes no
      // `--tile-size`). `transcodeNativeTile` defensively re-emits at
      // TILE_PX in case the producer config ever changes.
      tiles: [`${DEM_PROTOCOL}://${archiveUrl}#{z}/{x}/{y}`],
      tileSize: TILE_PX,
      encoding: "custom",
      redFactor: 65536,
      greenFactor: 256,
      blueFactor: 1,
      baseShift: 10000,
      minzoom,
      maxzoom,
    });
    installedSourceIds.add(sourceId);
    console.log(`[dem-source] added ${sourceId} exposed=${minzoom}..${maxzoom} archive_native=${archiveZ}`);
  }
  return packs;
}

/**
 * Idempotent: ensure a dedicated terrain-bound source exists for `pack`.
 * Same spec as the hillshade source — different MapLibre id, so the two
 * features don't alias one source (silences MapLibre's "same source for
 * hillshade and 3D terrain" warning and avoids the worker-pipeline
 * aliasing it documents as a quality regression).
 */
export function ensureTerrainSource(map: MlMap, pack: DemPack): string {
  registerProtocol();
  const sourceId = terrainSourceIdFor(pack);
  if (map.getSource(sourceId)) return sourceId;

  const archiveUrl = pack.archiveUrl;
  const archiveZ = pack.manifest.min_zoom;
  const minzoom = Math.max(0, archiveZ - DOWNSAMPLE_MAX_DELTA);
  const maxzoom = pack.manifest.max_zoom;
  archiveMinZoom.set(archiveUrl, archiveZ);

  map.addSource(sourceId, {
    type: "raster-dem",
    tiles: [`${DEM_PROTOCOL}://${archiveUrl}#{z}/{x}/{y}`],
    tileSize: TILE_PX,
    encoding: "custom",
    redFactor: 65536,
    greenFactor: 256,
    blueFactor: 1,
    baseShift: 10000,
    minzoom,
    maxzoom,
  });
  installedSourceIds.add(sourceId);
  console.log(`[dem-source] added terrain source ${sourceId} exposed=${minzoom}..${maxzoom}`);
  return sourceId;
}
